// src/Components/Auth/AdminLogin.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../LOGOS/Taglined Registered Logo.png';
import { Shield, Eye, EyeOff } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const superAdminEmail    = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
    const superAdminPassword = process.env.REACT_APP_SUPER_ADMIN_PASSWORD;

    if (
      formData.email.toLowerCase() === superAdminEmail?.toLowerCase() &&
      formData.password === superAdminPassword
    ) {
      sessionStorage.setItem('admin_access', 'true');
      sessionStorage.setItem('admin_email', formData.email);
      sessionStorage.setItem(
        'session_expiry',
        new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString()
      );
      navigate('/dashboard');
    } else {
      setError('Invalid email or password. Access denied.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-beetroot-dark to-gray-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Login Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={logo}
            alt="Beetroot Logo"
            className="h-16 w-auto"
          />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield className="text-beetroot-primary" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Admin Access</h1>
          <p className="text-gray-600 mt-2">Beetroot Analytics Dashboard</p>
          <div className="mt-3 inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-full text-sm">
            <Shield size={16} />
            <span className="font-medium">Super Admin Only</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 text-sm">
            <strong>Access Denied:</strong> {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@beetroot.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beetroot-primary focus:border-transparent transition"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your secure password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-beetroot-primary focus:border-transparent transition pr-12"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-beetroot-primary to-red-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Authenticating...
              </span>
            ) : (
              'Access Dashboard'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-center text-xs text-gray-500">
            🔒 Secure access for authorized personnel only
          </p>
          <p className="text-center text-xs text-gray-400 mt-2">
            All activities are logged and monitored
          </p>
        </div>
      </div>

      {/* Security Badge */}
      <div className="absolute bottom-4 right-4 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-xs">
        🛡️ SSL Encrypted Connection
      </div>
    </div>
  );
}